import { z } from 'zod';

/**
 * The in-app notifications behind the bell, as `GET /notifications` returns
 * them.
 *
 * The bell used to keep its own copy of this shape, and it drifted: it read a
 * `read_at` the server never sends, so every notification looked unread for
 * ever. One definition, which the API's responses are tested against and the
 * bell takes its types from, means the two cannot quietly disagree again.
 *
 * Self-contained (zod only) because `primitives.ts` re-exports this module so
 * the package root carries it: importing from there as well would be a cycle,
 * and this module would read the primitives before they existed.
 */

export const NotificationSchema = z.object({
  id: z.number().int().positive(),
  /**
   * What happened, as a dotted key — `ticket.assigned`, `sla.breached`,
   * `task.due_soon`. Open-ended on purpose: a kind the bell has not heard of
   * still shows, just without a colour of its own.
   */
  kind: z.string().min(1),
  title: z.string(),
  body: z.string().nullable(),
  /** The record it concerns, when there is one. */
  entity_type: z.string().nullable(),
  entity_id: z.number().int().positive().nullable(),
  /** Where it leads, when the server knows. One without leads nowhere, and is shown as a plain message. */
  url: z.string().nullable(),
  /** Who caused it, as worded at the time — the person may since have been renamed or removed. */
  actor_label: z.string().nullable(),
  read: z.boolean(),
  /** ISO-8601 instant, in UTC. */
  created_at: z.string().datetime({ offset: true }),
});
/**
 * One notification. Not called `Notification`, which is the browser's own
 * notification API and would be shadowed in every file that imported this.
 */
export type AppNotification = z.infer<typeof NotificationSchema>;

/** `GET /notifications`: the newest first, and how many are unread in all. */
export const NotificationListSchema = z.object({
  data: z.array(NotificationSchema),
  meta: z.object({
    /** Across every notification, not only the page returned. */
    unread: z.number().int().nonnegative(),
  }),
});
export type NotificationList = z.infer<typeof NotificationListSchema>;
